import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import {
	Button,
	ClassNameProps,
	dateDiff,
	DistributiveOmit,
	getObjectValues,
	Modal,
	omitKeys,
	Row,
	StyleProps,
	toGrammaticalNumber,
	useConditionalCloseConfirmation,
} from "@hex-insights/core";
import {
	anyFieldChanged,
	Form,
	FormEditButtonPair,
	FormState,
	settings,
	SubmitButton,
	useInternalField,
} from "@hex-insights/forms";
import {
	AnnouncementCreateMutation,
	AnnouncementDetailQuery,
	AnnouncementUpdateMutation,
	useInternalPortalsViewerInfoQuery,
} from "../../../Utilities";
import { AnnouncementModal, AuthorTimestampView, ModalAnnouncement } from "../../Components/Announcement";
import { AnnouncementFormState, AnnouncementFormValues } from "../../FormUtilities";
import { MediaAttachmentCustomForm } from "../MediaAttachment";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";
import styles from "./form.module.css";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<
	CreateProps<AnnouncementCreateMutation["createAnnouncement"]>,
	"formState" | "create"
> & {
	formStateArgs?: AnnouncementFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the Announcement model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

const placeholderAuthor = {
	id: "0",
	email: "",
	isCurrent: true,
	person: { id: "0", name: "M E", publicName: "M E", imageURL: "", imageKey: "", imageContentType: "" },
};

export type CreateProps<T> = {
	formState: FormState<AnnouncementFormValues.Create>;
	create: CreateFunction<AnnouncementFormValues.Create, T>;
	onSuccess: (formState: FormState<AnnouncementFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
	columnClassName?: string;
	columnStyle?: React.CSSProperties;
} & BaseFormProps<AnnouncementFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the Announcement model using the given form state.
 */
export function Create<T>({
	formState,
	create,
	onSuccess,
	formNameSuffix,
	noCloseConfirmation,
	columnClassName,
	columnStyle,
	...props
}: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "announcement.create" + (formNameSuffix ? "." + formNameSuffix : "");

	const { data } = useInternalPortalsViewerInfoQuery();
	const viewer = data?.viewer;

	const { formValues } = formState;
	const { startTime, endTime } = formValues;

	const anyChanged = React.useMemo(
		() =>
			anyFieldChanged<Omit<AnnouncementFormValues.Create, "endTime">>({
				formChanged: omitKeys(formState.formChanged, ["endTime"]),
				formSubFormStates: omitKeys(formState.formSubFormStates, ["endTime"]),
			}),
		[formState.formChanged, formState.formSubFormStates],
	);
	useConditionalCloseConfirmation(!noCloseConfirmation && anyChanged);

	const previewAnnouncement = React.useMemo<ModalAnnouncement>(
		() => ({
			...formValues,
			id: "preview",
			author: viewer ?? placeholderAuthor,
			startTime: formValues.startTime ?? new Date().toISOString(),
			mediaAttachments: formValues.mediaAttachments.map((e, i) => ({
				...e,
				id: `preview-${i}`,
				fileURL: e.fileKey ? settings.uploads.fileInfoToURL(e.fileKey, e.fileContentType) : "",
			})),
			userGroups: [],
		}),
		[viewer, formValues],
	);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Form
				name={formName}
				formState={formState}
				onSubmit={formState.onSubmitWrapper(onSubmit)}
				{...props}
				noCloseConfirmation
			>
				<Form.Column className={columnClassName} style={columnStyle}>
					<AuthorTimestampView announcement={previewAnnouncement} />

					<CreateFields formState={formState} />

					<ReleaseTimingNotice startTime={startTime} endTime={endTime} />

					<Row justify="spaced-end">
						<Button variant="secondary" onClick={openModal}>
							Preview
						</Button>
						<SubmitButton
							submissionStatus={formState.submissionStatus}
							onClick={formState.onSubmitWrapper(onSubmit)}
							disabled={getObjectValues(formState.submissionLocks).some((e) => e)}
						>
							Announce
						</SubmitButton>
					</Row>
				</Form.Column>
			</Form>

			<Modal.If condition={isModalOpen}>
				<AnnouncementModal
					announcement={previewAnnouncement}
					noReactions
					withBackButton
					isPreview
					backButtonText="Back to Form"
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type CreateFieldsProps = {
	formState: FormState<AnnouncementFormValues.Create>;
};

function CreateFields({ formState }: CreateFieldsProps) {
	useInternalField(formState, "title");

	return (
		<React.Fragment>
			<Row justify="center">
				<Field.SharingRecord.SubForm formState={formState} />
			</Row>
			<Field.Body formState={formState} placeholder="Write your announcement..." style={{ width: "100%" }} />
			<Field.MediaAttachments.SubForm formState={formState} style={{ width: "100%" }} />
			<Row justify="spaced-start" withVerticalSpacing overflow="wrap" style={{ width: "100%" }}>
				<Field.StartTime formState={formState} style={{ flexGrow: 1, width: "auto" }} />
				<Field.EndTime formState={formState} style={{ flexGrow: 1, width: "auto" }} />
			</Row>
		</React.Fragment>
	);
}

type ReleaseTimingNoticeProps = {
	startTime: AnnouncementFormValues.Base["startTime"];
	endTime: AnnouncementFormValues.Base["endTime"];
};

function ReleaseTimingNotice({ startTime, endTime }: ReleaseTimingNoticeProps) {
	const startTimeDate = startTime ? new Date(startTime) : new Date();
	const isStartTimeThisYear = startTimeDate.getFullYear() === new Date().getFullYear();
	const endTimeDate = endTime ? new Date(endTime) : null;
	const isEndTimeThisYear = endTimeDate?.getFullYear() === new Date().getFullYear();
	const duration = Math.round(dateDiff(endTimeDate ?? new Date(), startTimeDate, "hours", false));
	const durationDays = Math.floor(duration / 24);
	const durationHours = duration % 24;
	let durationText = "";
	if (durationDays === 0 && durationHours === 0) {
		durationText = "0 hours";
	} else if (durationDays === 0) {
		durationText = toGrammaticalNumber("hour", durationHours, true);
	} else if (durationHours === 0) {
		durationText = toGrammaticalNumber("day", durationDays, true);
	} else {
		durationText = `${toGrammaticalNumber("day", durationDays, true)} and ${toGrammaticalNumber(
			"hour",
			durationHours,
			true,
		)}`;
	}

	return (
		<div className={styles["release-timing-notice"]}>
			This announcement will be posted{startTime !== null ? " on" : ""}{" "}
			<span className={styles["release-timing-notice__primary-value"]}>
				{startTime === null
					? "now"
					: startTimeDate.toLocaleString(undefined, {
							year: isStartTimeThisYear ? undefined : "numeric",
							month: "short",
							day: "numeric",
							weekday: "short",
							hour: "numeric",
							minute: "2-digit",
					  })}
			</span>{" "}
			and will{" "}
			{endTimeDate === null ? (
				<span style={{ color: "var(--theme-colors__orange)" }}>never expire</span>
			) : (
				<React.Fragment>
					expire after <span className={styles["release-timing-notice__primary-value"]}>{durationText}</span> on{" "}
					<span className={styles["release-timing-notice__primary-value"]}>
						{endTimeDate.toLocaleDateString(undefined, {
							year: isEndTimeThisYear ? undefined : "numeric",
							month: "short",
							day: "numeric",
							weekday: "short",
							hour: "numeric",
							minute: "2-digit",
						})}
					</span>
				</React.Fragment>
			)}
			.
		</div>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<AnnouncementUpdateMutation["updateAnnouncement"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<AnnouncementFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the Announcement model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({ ...formStateArgs, announcement: props.announcement });

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	announcement: AnnouncementDetailQuery["announcement"];
	formState: FormState<AnnouncementFormValues.Detail>;
	update: UpdateFunction<AnnouncementFormValues.Detail, T>;
	onSuccess: (formState: FormState<AnnouncementFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<AnnouncementFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the Announcement model using the given form state.
 */
export function Detail<T>({ formState, announcement, update, onSuccess, formNameSuffix, ...props }: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `announcement.detail.${announcement.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column style={{ width: "100%" }}>
				<AuthorTimestampView announcement={announcement} />

				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} announcement={announcement} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<DistributiveOmit<AnnouncementFormState.UseReadOnlyFormStateArgs, "initialFormValues">>;
};

/**
 * Read-only detail form of the Announcement model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, announcement: props.announcement });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	announcement: AnnouncementDetailQuery["announcement"];
	formState: FormState<AnnouncementFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the Announcement model using the given form state.
 */
export function ReadOnly({ formState, announcement, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} announcement={announcement} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	announcement: AnnouncementDetailQuery["announcement"];
	formState: FormState<AnnouncementFormValues.Detail>;
};

function DetailFields({ formState, announcement }: DetailFieldsProps) {
	useInternalField(formState, "title");
	useInternalField(formState, "authorID");

	return (
		<React.Fragment>
			<Row justify="center">
				<Field.SharingRecord.Detail sharingRecord={announcement.sharingRecord} />
			</Row>
			<Field.Body formState={formState} style={{ width: "100%" }} />
			<MediaAttachmentCustomForm.ControlledMultiEditForm
				announcementID={announcement.id}
				mediaAttachments={announcement.mediaAttachments}
			/>
			<Row justify="spaced-start" withVerticalSpacing overflow="wrap" style={{ width: "100%" }}>
				<Field.StartTime formState={formState} style={{ flexGrow: 1, width: "auto" }} />
				<Field.EndTime formState={formState} style={{ flexGrow: 1, width: "auto" }} />
			</Row>
		</React.Fragment>
	);
}
